// src/pages/EmpleadoAusencias.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import SolicitarAusenciaForm from "../components/empleado/SolicitarAusenciaForm";
import MisAusencias from "../components/empleado/MisAusencias";
import AusenciasCalendario from "../components/empleado/AusenciasCalendario";
import SaldoVacacionesWidget from "../components/empleado/SaldoVacacionesWidget.jsx";

// ⬇️ IMPORT: usa el default del servicio
import ausenciasService from "../services/ausencias";

// ⬇️ AÑADIDO: api para /auth/me
import { api, API_URL, doLogout } from "../services/api";

import Header from "../components/shared/Header";

export default function EmpleadoAusencias({ session }) {
  const token = session?.token || session?.accessToken || null;
  const user = session?.user || {};
  const role = (session?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const userId = user?.id ?? null;
  const emailUsuario = user?.email || user || "";

  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const initialTab = ["solicitar", "mis", "saldos", "calendario"].includes(qs.get("tab"))
    ? qs.get("tab")
    : "solicitar";

  const [tab, setTab] = useState(initialTab);
  const [misAusencias, setMisAusencias] = useState([]);
  const [cargando, setCargando] = useState(false);

  const apiRoot = useMemo(() => {
    return (API_URL || "").replace(/\/+$/, "").replace(/\/api$/i, "");
  }, []);

  const goTab = (t) => {
    setTab(t);
    const p = new URLSearchParams(location.search);
    p.set("tab", t);
    navigate({ search: p.toString() }, { replace: true });
  };

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const next = ["solicitar", "mis", "saldos", "calendario"].includes(q.get("tab"))
      ? q.get("tab")
      : "solicitar";
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ---------- AÑADIDO: branding igual que en AdminPanel ----------
  const userObj = (typeof user === "object" && user) ? user : {};
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancel = false;
    async function loadMe() {
      if (userObj?.brand_dir && userObj?.company_name) return; // ya tenemos branding
      if (!token) return;
      try {
        const data = await api.get("/auth/me", token);
        if (!cancel && data?.user) setMe(data.user);
      } catch {
        try {
          if (cancel) return;
          const data2 = await api.get("/me", token);
          if (!cancel && data2?.user) setMe(data2.user);
        } catch { /* noop */ }
      }
    }
    loadMe();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userObj?.brand_dir, userObj?.company_name]);

  const brandForHeader = userObj?.brand_dir || me?.brand_dir || null;
  const companyForHeader = userObj?.company_name || me?.company_name || null;
  // ---------------------------------------------------------------

  // ⬇️ Carga robusta de datos
  async function cargarMias() {
    if (!token || !emailUsuario) return;
    setCargando(true);
    try {
      let feed = [];
      let base = [];

      if (typeof ausenciasService?.listarFeed === "function") {
        try {
          const tmp = await ausenciasService.listarFeed(token, { usuario: emailUsuario });
          if (Array.isArray(tmp)) feed = tmp;
        } catch (e) {
          console.warn("[EmpleadoAusencias] listarFeed falló, sigo:", e);
        }
      }

      if (typeof ausenciasService?.listarMiasPorEmail === "function") {
        try {
          const tmp = await ausenciasService.listarMiasPorEmail(token, emailUsuario);
          if (Array.isArray(tmp)) base = tmp;
        } catch (e) {
          console.warn("[EmpleadoAusencias] listarMiasPorEmail falló, intento con listarTodas:", e);
        }
      }

      if (!base.length && typeof ausenciasService?.listarTodas === "function") {
        try {
          const todas = await ausenciasService.listarTodas(token);
          const target = String(emailUsuario).trim().toLowerCase();
          base = (todas || []).filter(
            (a) => String(a?.usuario_email || "").trim().toLowerCase() === target
          );
        } catch (e) {
          console.warn("[EmpleadoAusencias] listarTodas fallback falló:", e);
        }
      }

      const byId = new Map();
      [...base, ...feed].forEach((row) => {
        const k =
          row?.id ??
          `${row?.kind || row?._request_kind || "AUSENCIA"}_${row?.fecha_inicio}_${row?.fecha_fin}_${row?.tipo}`;
        if (!byId.has(k)) byId.set(k, row);
      });

      setMisAusencias(Array.from(byId.values()));
    } catch (err) {
      console.error("Error cargando ausencias:", err);
      setMisAusencias([]);
    } finally {
      setCargando(false);
    }
  }

  async function handleLogout() {
    try {
      await doLogout();
    } catch {
      navigate("/login");
    }
  }

  useEffect(() => {
    if (tab === "mis" || tab === "calendario") cargarMias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/fondo.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/10 to-transparent" />

      {/* Header con el MISMO contrato que AdminPanel */}
      <Header
        usuario={emailUsuario}
        brandDir={brandForHeader}
        companyName={companyForHeader}
        onLogout={handleLogout}
        showSwitchVista={isAdmin}
        switchLabel="Vista admin"
        switchToPath="/admin"
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3 backdrop-blur-md bg-white/30 border border-white/40 rounded-2xl px-4 sm:px-5 py-3 shadow-lg">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#004B87] truncate">
              Ausencias y Vacaciones
            </h1>
            <p className="text-sm text-slate-700 truncate">{emailUsuario}</p>
          </div>
          {!isAdmin && (
            <button
              onClick={() => navigate("/")}
              className="px-3 sm:px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm shadow-md transition"
            >
              ⬅ Volver al inicio
            </button>
          )}
        </header>

        <div
          role="tablist"
          aria-label="Secciones de ausencias"
          className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mb-1"
        >
          {[
            { k: "solicitar", label: "Solicitar" },
            { k: "mis", label: "Mis solicitudes" },
            { k: "saldos", label: "Saldos" },
            { k: "calendario", label: "Calendario" },
          ].map((t) => {
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                role="tab"
                aria-selected={active}
                aria-controls={`panel-${t.k}`}
                tabIndex={active ? 0 : -1}
                onClick={() => goTab(t.k)}
                className={[
                  "px-3 sm:px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap",
                  active
                    ? "bg-indigo-600 text-white shadow"
                    : "bg-white/60 hover:bg-white/80 border border-slate-300 text-slate-700",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300",
                ].join(" ")}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <section
          className="backdrop-blur-lg bg-white/40 border border-white/50 rounded-2xl shadow-lg p-4 sm:p-6"
          aria-live="polite"
        >
          <div id="panel-solicitar" role="tabpanel" hidden={tab !== "solicitar"}>
            {tab === "solicitar" && (
              <div className="max-w-4xl mx-auto">
                <SolicitarAusenciaForm token={token} emailUsuario={emailUsuario} />
              </div>
            )}
          </div>

          <div id="panel-mis" role="tabpanel" hidden={tab !== "mis"}>
            {tab === "mis" && (
              <div className="max-w-5xl mx-auto">
                <MisAusencias
                  token={token}
                  emailUsuario={emailUsuario}
                  data={misAusencias}
                  cargando={cargando}
                  onRefrescar={cargarMias}
                />
              </div>
            )}
          </div>

          <div id="panel-saldos" role="tabpanel" hidden={tab !== "saldos"}>
            {tab === "saldos" && (
              <div className="max-w-3xl mx-auto">
                <SaldoVacacionesWidget />
              </div>
            )}
          </div>

          <div id="panel-calendario" role="tabpanel" hidden={tab !== "calendario"}>
            {tab === "calendario" && (
              <div className="max-w-5xl mx-auto">
                <AusenciasCalendario
                  data={misAusencias}
                  cargando={cargando}
                  onNeedData={cargarMias}
                  userId={userId}
                  apiRoot={apiRoot}
                />
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
